import { useCallback, useState } from 'react';
import {
	resetApplicationsListCache,
	useApplicationsList,
} from '../ApplicationsList/useApplicationsList';
import {
	ApplicationStatus,
	FROM_SCREENING_DASHBOARD,
} from '../../../shared/Constants';
import {
	ApplicationSummary,
	LocationState,
	UserRole,
} from '../../../shared/VolunteerApplicationServiceTypes';
import { useTranslation } from 'react-i18next';
import { useApplicationId } from '../../../hooks/useApplicationId';
import { FUNCTIONARY_HOME, INTERVIEW_LANDING } from '../../../shared/Routes';
import useUserSession from '../../../hooks/useUserSession';
import moment from 'moment';
import { useSnapshot } from 'valtio';
import { BatchInfoState } from '../../../components/BatchInfo.store';
import { useLocation } from 'react-router-dom';
import { useBatchId } from '../../../hooks/useBatchId';
import { useDashboardFilter } from './useDashboardFilter';
import { useDashboardCards } from './useDashboardCards';
import { useDashboardData } from './useDashboardData';

export const useScreeningDashboardPage = () => {
	const { i18n } = useTranslation();
	const isLengthyLanguage = i18n.language === 'ta' || i18n.language === 'ma';
	const { applicationsList, onPageChange, paging } = useApplicationsList({});
	const { selectedRole } = useUserSession();
	const location = useLocation();
	const { navigateToRoute } = useBatchId();
	const [applicationToVerify, setApplicationToVerify] =
		useState<ApplicationSummary>();
	const [openResultsModal, setOpenResultsModal] = useState(false);
	const { onFilterChange } = useApplicationsList({});
	const { navigateWithApplicationId } = useApplicationId();
	const isAdmin = selectedRole === UserRole.SNP_ADMIN;
	const isScreeningAdmin = selectedRole === UserRole.SCREENING_ADMIN;
	const isZC = selectedRole === UserRole.ZC;
	const isApprover = selectedRole === UserRole.APPROVER;
	const state = location.state as LocationState | null;
	const batchStateSnap = useSnapshot(BatchInfoState);
	const batchInfo = batchStateSnap.batchInfo;
	const screeningAdminRole =
		state?.from ??
		(isAdmin ? undefined : sessionStorage.getItem('screeningAdmin'));

	const dashboardFilterProps = useDashboardFilter();
	const dashboardCardProps = useDashboardCards();
	const dashboardDataProps = useDashboardData();

	const isValidDate = (dateStr?: string) =>
		dateStr &&
		dateStr !== '1970-01-01T00:00:00Z' &&
		moment().isSameOrAfter(dateStr);

	const handleBackFunctionaryClick = useCallback(() => {
		resetApplicationsListCache();
		if (screeningAdminRole === 'Screening Admin') {
			navigateToRoute(FUNCTIONARY_HOME);
			sessionStorage.removeItem('screeningAdmin');
			return;
		}

		const role =
			isAdmin || isScreeningAdmin
				? UserRole.SNP_ADMIN
				: isApprover
				? UserRole.APPROVER
				: isZC && isValidDate(batchInfo?.snpResultDateForZC)
				? UserRole.ZC
				: 'DEFAULT';

		switch (role) {
			case UserRole.SNP_ADMIN:
				onFilterChange({
					status: [ApplicationStatus.FUNCTIONARY_ASSIGNED],
				});
				break;

			case UserRole.APPROVER:
				onFilterChange({
					status: [ApplicationStatus.FUNCTIONARY_SUBMITTED],
				});
				break;

			case UserRole.ZC:
				onFilterChange({
					status: [ApplicationStatus.APPROVED],
				});
				break;

			default:
				onFilterChange({
					status: [
						ApplicationStatus.FUNCTIONARY_ASSIGNED,
						ApplicationStatus.FUNCTIONARY_SUBMITTED,
					],
				});
		}

		navigateToRoute(FUNCTIONARY_HOME);
	}, [
		screeningAdminRole,
		navigateToRoute,
		onFilterChange,
		isAdmin,
		isZC,
		isApprover,
		batchInfo?.snpResultDateForZC,
		isScreeningAdmin,
	]);

	const handleEvaluateClick = useCallback(
		(application: ApplicationSummary) => {
			navigateWithApplicationId(INTERVIEW_LANDING, application.applicationId, {
				state: { from: FROM_SCREENING_DASHBOARD },
			});
			sessionStorage.setItem('interviewOrigin', FROM_SCREENING_DASHBOARD);
		},
		[navigateWithApplicationId]
	);

	const handleVerifyResultsClick = useCallback(
		(application: ApplicationSummary) => {
			setApplicationToVerify(application);
			setOpenResultsModal(true);
		},
		[]
	);

	const screeningDashboardProps = {
		isLengthyLanguage,
		applicationsList,
		openResultsModal,
		applicationToVerify,
		paging,
		handleBackFunctionaryClick,
		handleEvaluateClick,
		handleVerifyResultsClick,
		setOpenResultsModal,
		onPageChange,
	};

	return {
		dashboardPageProps: {
			...screeningDashboardProps,
			...dashboardFilterProps,
			...dashboardCardProps,
			...dashboardDataProps,
		},
	};
};
